import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";
import { HealthCase } from "../components/HealthCase";

export default function Impact() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [index, setIndex] = useState([]);

  useSeo({
    title: page ? `${page.title} — KickStartKenyaFootball` : "Community Impact — KickStartKenyaFootball",
    description: page?.lead?.slice(0, 180) || "How football changes lives in Kenya: orphans and women.",
  });

  useEffect(() => {
    api.get("/impact").then((r) => setIndex(r.data)).catch(() => setIndex([]));
  }, []);

  useEffect(() => {
    if (!slug) return;
    setPage(null);
    api.get(`/impact/${slug}`).then((r) => setPage(r.data)).catch(() => setPage(null));
  }, [slug]);

  if (!slug)
    return (
      <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="impact-index">
        <div className="mx-auto max-w-[1500px] px-5 md:px-10">
          <p className="eyebrow">Community impact</p>
          <h1 className="display-1 mt-5 max-w-4xl">Community Impact</h1>
          <p className="body-copy mt-6 max-w-3xl">
            Football is not only a career ladder. For orphaned children and for women and girls escaping violence and
            poverty, it is a family, a refuge and a way back into education and work. These programmes are not add-ons to
            the plan — they are the reason the numbers on the other pages matter, and they are where the health savings,
            school attendance and community outcomes we cost elsewhere are actually earned.
          </p>
          <div className="mt-12 grid gap-px border border-zinc-800 bg-zinc-800 md:grid-cols-2">
            {index.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.06}>
                <Link
                  to={`/impact/${p.slug}`}
                  data-testid={`impact-card-${p.slug}`}
                  className="group flex h-full flex-col justify-between bg-[#0b0b0b] p-9 transition-colors hover:bg-[#151515] md:p-12"
                >
                  <span className="mono text-xs text-maasai">{String(i + 1).padStart(2, "0")}</span>
                  <div className="mt-10">
                    <h2 className="display-3 transition-colors group-hover:text-ochre">{p.title}</h2>
                    <p className="mt-4 text-sm uppercase tracking-[0.16em] text-zinc-500">{p.subtitle}</p>
                    <p className="body-copy mt-6 line-clamp-4">{p.lead}</p>
                  </div>
                  <span className="mt-9 inline-flex items-center gap-2 text-[0.64rem] font-bold uppercase tracking-[0.18em] text-ochre">
                    Read it <ArrowRight size={13} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
          <HealthCase heading="Fewer people in hospital, fewer days lost, more output" />
        </div>
      </div>
    );

  if (!page)
    return (
      <div className="flex min-h-[70svh] items-center justify-center bg-black px-5" data-testid="impact-loading">
        <p className="text-sm uppercase tracking-[0.2em] text-zinc-400">Loading</p>
      </div>
    );

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid={`impact-page-${page.slug}`}>
      <div className="mx-auto max-w-[1100px] px-5 md:px-10">
        <Reveal>
          <p className="eyebrow">{page.eyebrow}</p>
          <h1 className="display-1 mt-5">{page.title}</h1>
          <p className="mt-6 max-w-3xl font-display text-lg uppercase leading-snug tracking-tight text-ochre md:text-2xl">
            {page.subtitle}
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <p className="mt-10 border-l-2 border-maasai pl-6 text-base leading-relaxed text-white/85 md:text-lg">
            {page.lead}
          </p>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-10 border border-white/10 bg-[#0b0b0b] p-7 md:p-10" data-testid="impact-story">
            <p className="overline text-ochre">Why this is personal</p>
            <p className="body-copy mt-5">{page.story}</p>
          </div>
        </Reveal>

        <div className="mt-16">
          <p className="overline">{page.initiatives_title}</p>
          <div className="mt-6 border-t border-white/20" data-testid="impact-initiatives">
            {page.initiatives.map(([title, body], i) => (
              <Reveal key={title} delay={0.03}>
                <div
                  className="grid gap-4 border-b border-white/10 py-6 md:grid-cols-[56px_minmax(0,1fr)_minmax(0,1.6fr)] md:gap-8"
                  data-testid={`impact-initiative-${i}`}
                >
                  <span className="mono text-sm text-maasai">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display text-lg uppercase leading-tight tracking-tight text-white">{title}</h3>
                  <p className="text-sm leading-relaxed text-zinc-400">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.05}>
          <div className="mt-14 border-t-2 border-ochre bg-[#0b0b0b] p-8 md:p-11" data-testid="impact-broader">
            <p className="overline text-ochre">The broader impact</p>
            <p className="mt-5 text-base leading-relaxed text-white/85 md:text-lg">{page.broader_impact}</p>
          </div>
        </Reveal>

        <div className="mt-14 grid gap-px border border-zinc-800 bg-zinc-800 md:grid-cols-2">
          <div className="bg-[#0b0b0b] p-8" data-testid="impact-goals">
            <p className="overline">Measurable goals</p>
            <ul className="mt-6 space-y-4">
              {page.goals.map((g) => (
                <li key={g} className="flex gap-3 text-sm leading-relaxed text-zinc-300">
                  <span className="mt-[7px] h-1.5 w-1.5 shrink-0 bg-maasai" />
                  <span>{g}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-[#0b0b0b] p-8" data-testid="impact-call">
            <p className="overline text-ochre">{page.call_title}</p>
            <ul className="mt-6 space-y-4">
              {page.call.map((c) => (
                <li key={c} className="flex gap-3 text-sm leading-relaxed text-zinc-300">
                  <span className="mt-[7px] h-1.5 w-1.5 shrink-0 bg-ochre" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <Reveal delay={0.05}>
          <p className="body-copy mt-12 max-w-3xl">{page.closing}</p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link to="/apply" className="btn-primary" data-testid="impact-proposal-cta">
              Send a proposal <ArrowRight size={14} />
            </Link>
            <Link to="/plans" className="btn-ghost">
              The recommended steps
            </Link>
            <Link to="/impact" className="btn-ghost" data-testid="impact-back">
              All community impact pages
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
